/**
 * Centralized route constants to prevent typos and make route management easier
 */

// Public routes (no authentication required)
export const PUBLIC_ROUTES = {
  LOGIN: '/login',
  PENDING: '/pending',
} as const

// Protected routes (authentication required) - absolute paths
export const PROTECTED_ROUTES = {
  ROOT: '/',
  HR: '/hr',
  HR_CONVERSATIONS: '/hr/conversations',
  HR_DOCUMENTS: '/hr/documents',
  HR_WORK_AUTH: '/hr/work-auth/dashboard',
  HR_WORK_AUTH_BACKUPS: '/hr/work-auth/backups',
  OPS: '/ops',
  EVALUATIONS: '/evaluations',
  EVALUATION_DETAIL: '/evaluations/:testSetId',
  EVALUATION_RESULTS: '/evaluations/:testSetId/results',
  EVALUATION_ANALYTICS: '/evaluations/analytics',
  SETTINGS: '/settings',
  SETTINGS_USERS: '/settings/users',
  SETTINGS_DATABASE: '/settings/database',
  SETTINGS_SECURITY: '/settings/security',
  SETTINGS_NOTIFICATIONS: '/settings/notifications',
  SETTINGS_NOTIFICATION_TEMPLATES: '/settings/notification-templates',
  SETTINGS_APPEARANCE: '/settings/appearance',
  GLOSSARY_MANAGEMENT: '/glossary',
  ERROR_TEST: '/error-test',
} as const

// Nested route segments (for React Router nested routing)
export const NESTED_ROUTES = {
  HR: 'hr',
  HR_CONVERSATIONS: 'hr/conversations',
  HR_DOCUMENTS: 'hr/documents',
  OPS: 'ops',
  EVALUATIONS: 'evaluations',
  EVALUATION_DETAIL: 'evaluations/:testSetId',
  EVALUATION_RESULTS: 'evaluations/:testSetId/results',
  EVALUATION_ANALYTICS: 'evaluations/analytics',
  SETTINGS: 'settings',
  GLOSSARY_MANAGEMENT: 'glossary',
  ERROR_TEST: 'error-test',
} as const

// All routes combined for easy access
export const ROUTES = {
  ...PUBLIC_ROUTES,
  ...PROTECTED_ROUTES,
} as const

// Route paths array for validation/iteration
export const ALL_ROUTE_PATHS = Object.values(ROUTES)

// Type for route validation
export type RoutePath = typeof ROUTES[keyof typeof ROUTES]
export type NestedRoutePath = typeof NESTED_ROUTES[keyof typeof NESTED_ROUTES]

// Helper function to check if a path is a valid route
export const isValidRoute = (path: string): path is RoutePath => {
  return ALL_ROUTE_PATHS.includes(path as RoutePath)
}

// Navigation items configuration
export const NAVIGATION_CONFIG = {
  HR_AGENT: {
    name: 'HR Agent',
    path: PROTECTED_ROUTES.HR,
    nestedPath: NESTED_ROUTES.HR,
  },
  OPS_AGENT: {
    name: 'Operations Agent', 
    path: PROTECTED_ROUTES.OPS,
    nestedPath: NESTED_ROUTES.OPS,
  },
  EVALUATIONS: {
    name: 'Evaluations',
    path: PROTECTED_ROUTES.EVALUATIONS,
    nestedPath: NESTED_ROUTES.EVALUATIONS,
  },
  SETTINGS: {
    name: 'Settings',
    path: PROTECTED_ROUTES.SETTINGS,
    nestedPath: NESTED_ROUTES.SETTINGS,
  },
  GLOSSARY_MANAGEMENT: {
    name: 'Glossary',
    path: PROTECTED_ROUTES.GLOSSARY_MANAGEMENT,
    nestedPath: NESTED_ROUTES.GLOSSARY_MANAGEMENT,
  },
} as const

// Route configuration for React Router
export const ROUTE_CONFIG = {
  public: {
    login: {
      path: PUBLIC_ROUTES.LOGIN,
      element: 'LoginPage'
    }
  },
  protected: {
    root: {
      path: PROTECTED_ROUTES.ROOT,
      element: 'MainLayout',
      children: {
        index: {
          element: 'RootRedirect'
        },
        hr: {
          path: NESTED_ROUTES.HR,
          element: 'HRAgentPage',
          roleRequirement: 'adminRoles'
        },
        ops: {
          path: NESTED_ROUTES.OPS,
          element: 'OpsAgentPage', 
          roleRequirement: 'managerRoles'
        },
        settings: {
          path: NESTED_ROUTES.SETTINGS,
          element: 'SettingsPage',
          roleRequirement: 'systemAdminOnly'
        }
      }
    }
  }
} as const 